<script>
import { mapGetters } from 'vuex';
import { validEmailsByComma } from './helpers/emailHeadHelper';
import { useVuelidate } from '@vuelidate/core';

const REPLY_PREFIX = /^(re|res|fw|fwd|enc)\s*:/i;

export default {
  props: {
    ccEmails: {
      type: String,
      default: '',
    },
    bccEmails: {
      type: String,
      default: '',
    },
    toEmails: {
      type: String,
      default: '',
    },
  },
  emits: ['update:bccEmails', 'update:ccEmails', 'update:toEmails', 'preview'],
  setup() {
    return { v$: useVuelidate() };
  },
  data() {
    return {
      showCc: false,
      showBcc: false,
      ccEmailsVal: '',
      bccEmailsVal: '',
      toEmailsVal: '',
    };
  },
  computed: {
    ...mapGetters({ currentChat: 'getSelectedChat' }),
    senderEmail() {
      const inboxId = this.currentChat?.inbox_id;
      if (!inboxId) return '';
      return this.$store.getters['inboxes/getInbox'](inboxId)?.email || '';
    },
    replySubject() {
      const subject = String(
        this.currentChat?.additional_attributes?.mail_subject || ''
      ).trim();
      if (!subject) return '';
      return REPLY_PREFIX.test(subject) ? subject : `Re: ${subject}`;
    },
    isCcVisible() {
      return this.showCc || Boolean(this.ccEmailsVal);
    },
    isBccVisible() {
      return this.showBcc || Boolean(this.bccEmailsVal);
    },
    fields() {
      return [
        {
          key: 'toEmailsVal',
          label: this.$t('CONVERSATION.REPLYBOX.EMAIL_HEAD.TO'),
          placeholder: this.$t(
            'CONVERSATION.REPLYBOX.EMAIL_HEAD.CC.PLACEHOLDER'
          ),
          error: this.$t('CONVERSATION.REPLYBOX.EMAIL_HEAD.CC.ERROR'),
          // Sempre visível: mostra (e permite trocar) para quem a resposta vai.
          visible: true,
        },
        {
          key: 'ccEmailsVal',
          label: this.$t('CONVERSATION.REPLYBOX.EMAIL_HEAD.CC.LABEL'),
          placeholder: this.$t(
            'CONVERSATION.REPLYBOX.EMAIL_HEAD.CC.PLACEHOLDER'
          ),
          error: this.$t('CONVERSATION.REPLYBOX.EMAIL_HEAD.CC.ERROR'),
          visible: this.isCcVisible,
          removable: 'cc',
        },
        {
          key: 'bccEmailsVal',
          label: this.$t('CONVERSATION.REPLYBOX.EMAIL_HEAD.BCC.LABEL'),
          placeholder: this.$t(
            'CONVERSATION.REPLYBOX.EMAIL_HEAD.BCC.PLACEHOLDER'
          ),
          error: this.$t('CONVERSATION.REPLYBOX.EMAIL_HEAD.BCC.ERROR'),
          visible: this.isBccVisible,
          removable: 'bcc',
        },
      ].filter(field => field.visible);
    },
  },
  watch: {
    bccEmails(newVal) {
      if (newVal !== this.bccEmailsVal) {
        this.bccEmailsVal = newVal;
      }
    },
    ccEmails(newVal) {
      if (newVal !== this.ccEmailsVal) {
        this.ccEmailsVal = newVal;
      }
    },
    toEmails(newVal) {
      if (newVal !== this.toEmailsVal) {
        this.toEmailsVal = newVal;
      }
    },
  },
  mounted() {
    this.ccEmailsVal = this.ccEmails;
    this.bccEmailsVal = this.bccEmails;
    this.toEmailsVal = this.toEmails;
  },
  validations: {
    ccEmailsVal: {
      hasValidEmails(value) {
        return validEmailsByComma(value);
      },
    },
    bccEmailsVal: {
      hasValidEmails(value) {
        return validEmailsByComma(value);
      },
    },
    toEmailsVal: {
      hasValidEmails(value) {
        return validEmailsByComma(value);
      },
    },
  },
  methods: {
    revealField(type) {
      if (type === 'cc') this.showCc = true;
      else this.showBcc = true;
      this.$nextTick(() => {
        this.$refs[`input-${type}`]?.[0]?.focus();
      });
    },
    // Hiding a copy field also clears it, so nothing is sent to recipients the agent no longer sees.
    removeField(type) {
      if (type === 'cc') {
        this.showCc = false;
        this.ccEmailsVal = '';
      } else {
        this.showBcc = false;
        this.bccEmailsVal = '';
      }
      this.onBlur();
    },
    onBlur() {
      this.v$.$touch();
      this.$emit('update:bccEmails', this.bccEmailsVal);
      this.$emit('update:ccEmails', this.ccEmailsVal);
      this.$emit('update:toEmails', this.toEmailsVal);
    },
  },
};
</script>

<template>
  <section
    class="reply-email-head"
    :aria-label="$t('CONVERSATION.REPLYBOX.EMAIL_HEAD.TITLE')"
  >
    <div v-if="senderEmail || replySubject" class="reply-email-head__meta">
      <p v-if="senderEmail" class="reply-email-head__meta-item">
        <span class="reply-email-head__meta-label">
          {{ $t('CONVERSATION.REPLYBOX.EMAIL_HEAD.FROM') }}
        </span>
        <span class="truncate" :title="senderEmail">{{ senderEmail }}</span>
      </p>
      <p v-if="replySubject" class="reply-email-head__meta-item min-w-0 flex-1">
        <span class="reply-email-head__meta-label">
          {{ $t('CONVERSATION.REPLYBOX.EMAIL_HEAD.SUBJECT') }}
        </span>
        <span
          class="truncate font-medium text-n-slate-12"
          :title="replySubject"
        >
          {{ replySubject }}
        </span>
      </p>
    </div>

    <div
      v-for="(field, index) in fields"
      :key="field.key"
      class="reply-email-head__row"
      :class="{ 'has-error': v$[field.key].$error }"
    >
      <label :for="`reply-email-${field.key}`" class="reply-email-head__label">
        {{ field.label }}
      </label>
      <input
        :id="`reply-email-${field.key}`"
        :ref="field.removable ? `input-${field.removable}` : undefined"
        v-model="v$[field.key].$model"
        type="text"
        inputmode="email"
        autocomplete="off"
        class="reply-email-head__input"
        :placeholder="field.placeholder"
        :aria-invalid="v$[field.key].$error"
        :aria-describedby="
          v$[field.key].$error ? `reply-email-${field.key}-error` : undefined
        "
        @blur="onBlur"
      />
      <div class="reply-email-head__actions">
        <template v-if="index === 0">
          <button
            v-if="!isCcVisible"
            v-tooltip.top="$t('CONVERSATION.REPLYBOX.EMAIL_HEAD.CC.TOOLTIP')"
            type="button"
            class="reply-email-head__chip"
            :aria-label="$t('CONVERSATION.REPLYBOX.EMAIL_HEAD.CC.TOOLTIP')"
            @click="revealField('cc')"
          >
            {{ $t('CONVERSATION.REPLYBOX.EMAIL_HEAD.CC.LABEL') }}
          </button>
          <button
            v-if="!isBccVisible"
            v-tooltip.top="$t('CONVERSATION.REPLYBOX.EMAIL_HEAD.BCC.TOOLTIP')"
            type="button"
            class="reply-email-head__chip"
            :aria-label="$t('CONVERSATION.REPLYBOX.EMAIL_HEAD.BCC.TOOLTIP')"
            @click="revealField('bcc')"
          >
            {{ $t('CONVERSATION.REPLYBOX.EMAIL_HEAD.BCC.LABEL') }}
          </button>
          <button
            type="button"
            class="reply-email-head__chip"
            data-test="email-preview"
            @click="$emit('preview')"
          >
            {{ $t('CONVERSATION.REPLYBOX.EMAIL_HEAD.PREVIEW') }}
          </button>
        </template>
        <button
          v-if="field.removable"
          type="button"
          class="reply-email-head__remove"
          :title="$t('CONVERSATION.REPLYBOX.EMAIL_HEAD.REMOVE_FIELD')"
          :aria-label="`${$t('CONVERSATION.REPLYBOX.EMAIL_HEAD.REMOVE_FIELD')} ${field.label}`"
          @click="removeField(field.removable)"
        >
          <span
            class="i-hugeicons-cancel-01 size-3.5 shrink-0"
            aria-hidden="true"
          />
        </button>
      </div>
      <p
        v-if="v$[field.key].$error"
        :id="`reply-email-${field.key}-error`"
        class="reply-email-head__error"
      >
        {{ field.error }}
      </p>
    </div>

    <div
      v-if="!fields.length"
      class="reply-email-head__row reply-email-head__row--actions"
    >
      <span class="reply-email-head__label" />
      <div class="reply-email-head__actions ms-auto">
        <button
          v-tooltip.top="$t('CONVERSATION.REPLYBOX.EMAIL_HEAD.CC.TOOLTIP')"
          type="button"
          class="reply-email-head__chip"
          :aria-label="$t('CONVERSATION.REPLYBOX.EMAIL_HEAD.CC.TOOLTIP')"
          @click="revealField('cc')"
        >
          {{ $t('CONVERSATION.REPLYBOX.EMAIL_HEAD.CC.LABEL') }}
        </button>
        <button
          v-tooltip.top="$t('CONVERSATION.REPLYBOX.EMAIL_HEAD.BCC.TOOLTIP')"
          type="button"
          class="reply-email-head__chip"
          :aria-label="$t('CONVERSATION.REPLYBOX.EMAIL_HEAD.BCC.TOOLTIP')"
          @click="revealField('bcc')"
        >
          {{ $t('CONVERSATION.REPLYBOX.EMAIL_HEAD.BCC.LABEL') }}
        </button>
      </div>
    </div>
  </section>
</template>

<style lang="scss" scoped>
.reply-email-head {
  @apply mb-2 overflow-hidden rounded-xl border border-solid border-n-weak bg-n-solid-1;
}

.reply-email-head__meta {
  @apply flex flex-wrap items-center gap-x-5 gap-y-1 border-b border-solid border-n-weak bg-n-alpha-1 px-3 py-2;
}

.reply-email-head__meta-item {
  @apply mb-0 flex min-w-0 items-center gap-2 text-xs text-n-slate-11;
}

.reply-email-head__meta-label {
  @apply shrink-0 text-[0.6875rem] font-semibold uppercase tracking-wide text-n-slate-10;
}

.reply-email-head__row {
  @apply flex flex-wrap items-center gap-x-2 border-b border-solid border-n-weak px-3 last:border-b-0;

  &.has-error {
    @apply bg-n-ruby-2;

    .reply-email-head__label {
      @apply text-n-ruby-11;
    }
  }
}

.reply-email-head__row--actions {
  @apply py-3;
}

.reply-email-head__label {
  @apply w-9 shrink-0 text-xs font-semibold text-n-slate-11;
}

.reply-email-head__input {
  @apply m-0 h-9 min-w-0 flex-1 border-0 bg-transparent p-0 text-sm text-n-slate-12 shadow-none outline-none placeholder:text-n-slate-10 focus:border-0 focus:shadow-none focus:outline-none dark:bg-transparent #{!important};
}

.reply-email-head__actions {
  @apply flex shrink-0 items-center gap-1;
}

.reply-email-head__chip {
  @apply inline-flex h-6 items-center rounded-md border border-solid border-n-weak bg-n-solid-2 px-2 text-xs font-medium text-n-slate-11 transition-colors hover:border-n-brand hover:text-n-brand focus-visible:outline focus-visible:outline-2 focus-visible:outline-n-brand;
}

.reply-email-head__remove {
  @apply inline-flex size-6 shrink-0 items-center justify-center rounded-md p-0 text-n-slate-10 transition-colors hover:bg-n-alpha-2 hover:text-n-slate-12 focus-visible:outline focus-visible:outline-2 focus-visible:outline-n-brand;
}

.reply-email-head__error {
  @apply mb-2 mt-0 w-full ps-11 text-xs text-n-ruby-11;
}
</style>
